/**
 * 911 CAD store — the daily activity board.
 *
 * Separate from Incident Reports (serious events) but linkable: a CAD call can
 * be flagged for incident and later claimed by an incident record. This store
 * owns the call log, the reference lists (call types + businesses, both served
 * from D1 so they extend without code changes), and create/update actions.
 *
 * Status is authoritative from the server, which derives it from the time
 * fields (finished -> Completed, dispatched -> Dispatched, else Received).
 */
import { create } from 'zustand'
import { workerGetV1, workerPostV1, workerPutV1 } from '../api/worker'

export interface CadCall {
  id: number
  control_number: string
  received_ts: string
  entered_by: string
  location: string
  business_name: string
  beat: string
  type_call: string
  additional_info: string
  complainant: string
  contact_at: string
  notes: string
  unit_assigned: string
  backup_unit: string
  dispatched: string
  arrived: string
  finished: string
  disposition: string
  status: 'Received' | 'Dispatched' | 'Completed'
  flagged_for_incident?: number
  incident_id?: number | null
}

export interface CadBusiness { name: string; beat: string; address: string; wing?: string; space?: string; phone?: string; vacant?: number }
export interface CadRosterUnit { call_sign: string; name: string; rank: string }

/** Fields the call card form can submit (everything except server-managed cols) */
export type CadCallInput = Partial<Omit<CadCall, 'id' | 'control_number' | 'received_ts' | 'status'>>

interface CadState {
  calls: CadCall[]
  types: string[]
  businesses: CadBusiness[]
  roster: CadRosterUnit[]
  loading: boolean
  refsLoaded: boolean
  error: string

  loadRefs: () => Promise<void>
  loadCalls: (dateYYYYMMDD?: string) => Promise<void>
  createCall: (input: CadCallInput) => Promise<CadCall | null>
  updateCall: (id: number, input: CadCallInput) => Promise<CadCall | null>
}

export const useCadStore = create<CadState>((set, get) => ({
  calls: [],
  types: [],
  businesses: [],
  roster: [],
  loading: false,
  refsLoaded: false,
  error: '',

  async loadRefs() {
    if (get().refsLoaded) return
    try {
      const res = await workerGetV1<{ ok: boolean; types: string[]; businesses: CadBusiness[]; roster: CadRosterUnit[] }>('/api/v1/cad/refs')
      set({ types: res.types || [], businesses: res.businesses || [], roster: res.roster || [], refsLoaded: true })
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load CAD reference lists' })
    }
  },

  async loadCalls(dateYYYYMMDD) {
    set({ loading: true, error: '' })
    try {
      const params = dateYYYYMMDD ? { date: dateYYYYMMDD } : undefined
      const res = await workerGetV1<{ ok: boolean; calls: CadCall[] }>('/api/v1/cad', params)
      set({ calls: res.calls || [], loading: false })
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : 'Failed to load calls' })
    }
  },

  async createCall(input) {
    try {
      const res = await workerPostV1<{ ok: boolean; call: CadCall }>('/api/v1/cad', input as Record<string, unknown>)
      if (res.call) {
        set(s => ({ calls: [res.call, ...s.calls] }))
        return res.call
      }
      return null
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to create call' })
      return null
    }
  },

  async updateCall(id, input) {
    try {
      const res = await workerPutV1<{ ok: boolean; call: CadCall }>(`/api/v1/cad/${id}`, input as Record<string, unknown>)
      if (res.call) {
        set(s => ({ calls: s.calls.map(c => c.id === id ? res.call : c) }))
        return res.call
      }
      return null
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to update call' })
      return null
    }
  },
}))
